import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, Shield, Target } from "lucide-react";
import { api } from "../../lib/api";
import { SIDES, SIDE_COLOR, getArchetype, secondaryBlockReason, useArchetypeLibrary } from "../../lib/archetypes";
import { ArchetypeCardGrid } from "./ArchetypeCardGrid";
import { ArchetypeIdentityCard } from "./ArchetypeIdentityCard";

const ICON = { offense: Target, defense: Shield };
const empty = (a) => ({ offense: { primary: a?.offense?.primary || null, secondary: a?.offense?.secondary || null }, defense: { primary: a?.defense?.primary || null, secondary: a?.defense?.secondary || null }, overrideNote: a?.overrideNote || "" });

// Admin edit panel per side — collapsed by default like the pillar panels; cards for Primary then Secondary (pairing rules enforced).
function SideEditor({ side, label, cur, onPick, fits }) {
  const [open, setOpen] = useState(false);
  const Icon = ICON[side]; const color = SIDE_COLOR[side];
  const chosen = [cur.primary, cur.secondary].filter(Boolean);
  return (
    <div className="border border-white/15 bg-[#1c1c1c] p-5" data-testid={`archetype-edit-${side}`}>
      <div className={open ? "border-b border-white/10 pb-3 mb-4" : ""}>
        <div className="flex items-center gap-2.5 flex-wrap">
          <button onClick={() => setOpen(o => !o)} className="flex items-center gap-2.5 text-left" data-testid={`archetype-edit-${side}-toggle`}>
            {open ? <ChevronDown className="w-4 h-4 text-[#757575]" /> : <ChevronRight className="w-4 h-4 text-[#757575]" />}
            <Icon className="w-4 h-4 shrink-0" style={{ color }} />
            <div className="font-heading text-lg uppercase tracking-wide text-white">Edit {label}</div>
          </button>
          <div className="ml-auto text-[10px] uppercase tracking-widest text-[#a0a0a0]" data-testid={`archetype-edit-${side}-current`}>
            {cur.primary ? <><span className="text-white font-bold">{getArchetype(cur.primary).name}</span>{cur.secondary && <> · <span className="text-white">{getArchetype(cur.secondary).name}</span></>}</> : "Not defined"}
          </div>
        </div>
      </div>
      {open && (
        <div className="space-y-5">
          <div>
            <div className="text-[9px] uppercase tracking-[0.35em] text-[#a0a0a0] mb-2">Primary <span className="text-[#575757]">· required · who they are most of the time</span></div>
            <ArchetypeCardGrid side={side} slot="primary" value={cur.primary} primaryId={cur.primary} chosenIds={chosen} editable onChange={(id) => onPick("primary", id)} color={color} testid={`archetype-primary-${side}`} fits={fits} />
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-[0.35em] text-[#a0a0a0] mb-2">Secondary <span className="text-[#575757]">· optional · the other way they create value</span></div>
            <ArchetypeCardGrid side={side} slot="secondary" value={cur.secondary} primaryId={cur.primary} chosenIds={chosen} editable onChange={(id) => onPick("secondary", id)} color={color} testid={`archetype-secondary-${side}`} fits={fits} />
          </div>
        </div>
      )}
    </div>
  );
}

// Step 3 · Define: admin picks Offense + Defense archetypes (autosaves); everyone sees the two-section summary.
export function ArchetypeDefine({ playerId, assessment, canEdit, onAssessmentChange }) {
  const lib = useArchetypeLibrary();
  const [sel, setSel] = useState(() => empty(assessment?.archetypes));
  const [saving, setSaving] = useState("");
  const noteTimer = useRef(null);
  const [fits, setFits] = useState(null);
  const aid = assessment?.id;
  const rkey = (assessment?.ratings || []).map(r => `${r.skillId}:${r.rating}:${r.isPrimary ? 1 : 0}`).sort().join("|");
  useEffect(() => { setSel(empty(assessment?.archetypes)); }, [aid]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!aid) { setFits(null); return; }
    let live = true;
    api.archetypeFit(playerId, aid).then(d => live && setFits(d.fits)).catch(() => live && setFits(null));
    return () => { live = false; };
  }, [playerId, aid, rkey]);

  const persist = async (next) => {
    setSaving("Saving…");
    try { const doc = await api.saveArchetypes(playerId, aid, next); onAssessmentChange?.(doc); setSaving("Saved"); }
    catch (e) { setSaving(""); toast.error(e?.response?.data?.detail || "Could not save archetypes"); }
  };
  const update = (patch, debounce = false) => {
    const next = { ...sel, ...patch }; setSel(next); clearTimeout(noteTimer.current);
    if (debounce) noteTimer.current = setTimeout(() => persist(next), 600); else persist(next);
  };
  const pick = (side, slot, id) => {
    const cur = { ...sel[side], [slot]: id };
    if (slot === "primary" && cur.secondary && (!id || secondaryBlockReason(id, getArchetype(cur.secondary)))) cur.secondary = null;
    update({ [side]: cur });
  };

  if (!assessment) return <div className="text-[#757575] text-sm py-2" data-testid="archetype-empty">{canEdit ? "Start an assessment in Step 1 to define archetypes." : "No archetypes defined yet."}</div>;
  if (!lib.ready) return <div className="text-[#757575] text-xs" data-testid="archetype-library-loading">{lib.error || "Loading archetypes…"}</div>;

  const a = assessment.archetypes;
  return (
    <div className="space-y-4" data-testid="archetype-define">
      {canEdit && (
        <div className="space-y-3" data-testid="archetype-editors">
          {SIDES.map(([side, label]) => <SideEditor key={side} side={side} label={label} cur={sel[side]} onPick={(slot, id) => pick(side, slot, id)} fits={fits} />)}
          <div className="flex flex-wrap items-start gap-4">
            <label className="flex-1 min-w-[260px]">
              <div className="text-[9px] uppercase tracking-[0.35em] text-[#a0a0a0] mb-1">Coach note <span className="text-[#575757]">· optional · shown to coaches only</span></div>
              <textarea value={sel.overrideNote} onChange={(e) => update({ overrideNote: e.target.value }, true)} rows={2} placeholder="e.g. Plays as a Slasher on his AAU team; we're building toward Shot Creator."
                className="w-full bg-[#141414] border border-white/10 text-sm text-white p-2 placeholder:text-[#575757] focus:outline-none focus:border-white/30" data-testid="archetype-override-note" />
            </label>
            <div className="text-[10px] text-[#757575] pt-5" data-testid="archetype-save-state">{saving || (a?.confirmedAt ? `Confirmed by ${a.confirmedBy || "coach"} · ${new Date(a.confirmedAt).toLocaleDateString()}` : "Not set yet")}</div>
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="archetype-summary">
        {SIDES.map(([side, label]) => <ArchetypeIdentityCard key={side} side={side} label={label} sel={canEdit ? sel[side] : a?.[side]} fits={fits} />)}
      </div>
    </div>
  );
}
